import { useQuery } from '@tanstack/react-query';
import { fetchPublicFaq } from '@/api/faq';
import { fetchPublicProducts, type Product } from '@/api/products';
import { queryKeys } from '@/api/queryKeys';
import { FAQ_ITEMS, RATES, type FaqItem, type RateItem } from '@/data/homeContent';
import { formatForint } from '@/lib/utils';

const CATEGORY_KIND: Record<Product['category'], string> = {
  BED: 'Főeszköz',
  MATTRESS: 'Matrac',
  ACCESSORY: 'Kiegészítő az ágy mellé'
};

export const productsToRates = (products: Product[]): RateItem[] => {
  const main = products
    .filter((product) => product.category !== 'ACCESSORY')
    .map<RateItem>((product) => ({
      name: product.name,
      kind: product.shortDescription ?? CATEGORY_KIND[product.category],
      description: product.description ?? '',
      priceLabel: formatForint(product.monthlyPrice),
      unitLabel: '/ hó',
      href: `/termekek/${product.slug}`
    }));

  const accessories = products.filter((product) => product.category === 'ACCESSORY');

  if (accessories.length === 0) return main;

  const cheapest = Math.min(...accessories.map((product) => product.monthlyPrice));
  const names = accessories.map((product) => product.name).join(', ');

  return [
    ...main,
    {
      name: 'Betegápolási segédeszközök',
      kind: 'Kiegészítők az ágy mellé',
      description: `${names}. Egy rendelésben, egy címre, egy szállítási díjjal.`,
      priceLabel: `${formatForint(cheapest)}-tól`,
      unitLabel: '/ hó, eszközönként',
      href: '/termekek#kiegeszitok'
    }
  ];
};

export const useRates = () => {
  const query = useQuery({
    queryKey: queryKeys.publicProducts,
    queryFn: fetchPublicProducts,
    staleTime: 5 * 60_000
  });

  const rates: RateItem[] = query.data && query.data.length > 0 ? productsToRates(query.data) : RATES;

  return { rates, fromServer: Boolean(query.data && query.data.length > 0), isLoading: query.isLoading };
};

export const useFaq = () => {
  const query = useQuery({
    queryKey: queryKeys.publicFaq,
    queryFn: fetchPublicFaq,
    staleTime: 5 * 60_000
  });

  const items: FaqItem[] =
    query.data && query.data.length > 0
      ? query.data.map((entry) => ({ question: entry.question, answer: entry.answer }))
      : FAQ_ITEMS;

  return { items, fromServer: Boolean(query.data && query.data.length > 0), isLoading: query.isLoading };
};
